import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Empty, Tag, Typography } from 'antd';
import {
  ProjectOutlined,
  FileProtectOutlined,
  TeamOutlined,
  FunnelPlotOutlined,
  TrophyOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  EnvironmentOutlined,
  ArrowRightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMockStore } from '@/mocks/store';
import { semantic } from '@/theme/tokens';

const { Text } = Typography;

/**
 * Universal jump-to-anything command palette (⌘K / Ctrl+K).
 *
 * Behaviour:
 *   - Global keyboard shortcut: Cmd/Ctrl+K opens, Esc closes
 *   - Empty input shows "Recents" (last 8 visited items, persisted)
 *   - Typing 1+ char performs case-insensitive substring search across
 *     pages + projects + contracts + employees + leads + tenders
 *   - Up/Down arrows move selection, Enter navigates, Tab also closes
 *   - Each result row: icon + label + subtitle (secondary text) +
 *     category badge on the right
 *
 * Index is built LAZILY when the modal opens (not at app boot) — keeps
 * the cost off the main startup path. Re-built on each open from the
 * current mock store snapshot, so reset / fixture changes are live.
 */

type CommandCategory = 'page' | 'project' | 'contract' | 'employee' | 'lead' | 'tender';

interface CommandItem {
  id: string;
  label: string;
  /** Secondary text shown under the label (location, status, etc.) */
  detail?: string;
  category: CommandCategory;
  /** Lead icon — colour comes from the category palette */
  iconKind: CommandCategory;
  /** Absolute hash route to navigate to */
  path: string;
  /** Extra search terms not visible in the row */
  keywords?: string[];
}

const CATEGORY_META: Record<
  CommandCategory,
  { label: string; color: string; icon: ReactNode }
> = {
  page: { label: 'Page', color: semantic.brand.primary, icon: <AppstoreOutlined /> },
  project: { label: 'Project', color: semantic.status.success, icon: <ProjectOutlined /> },
  contract: { label: 'Contract', color: semantic.brand.accent, icon: <FileProtectOutlined /> },
  employee: { label: 'Person', color: semantic.status.info, icon: <TeamOutlined /> },
  lead: { label: 'Lead', color: '#9C3961', icon: <FunnelPlotOutlined /> },
  tender: { label: 'Tender', color: '#6E4F8B', icon: <TrophyOutlined /> },
};

/** Hard-coded page index — keeps in lock-step with the primary rail. */
const PAGE_INDEX: Array<Omit<CommandItem, 'id' | 'iconKind' | 'category'> & { keywords?: string[] }> = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Finance', path: '/finance', keywords: ['money', 'cash', 'gl'] },
  { label: 'Engineering', path: '/engineering', keywords: ['drawings', 'projects', 'bim'] },
  { label: 'Operations', path: '/operations', keywords: ['site', 'progress'] },
  { label: 'Contracts & Commercial', path: '/contracts', keywords: ['vo', 'ipc', 'claims'] },
  { label: 'HR & Administration', path: '/hr', keywords: ['employees', 'payroll', 'leave'] },
  { label: 'Marketing & BD', path: '/marketing', keywords: ['leads', 'tenders', 'pipeline'] },
  { label: 'Legal', path: '/legal', keywords: ['redlining', 'compliance', 'disputes'] },
  { label: 'Workflows', path: '/workflows', keywords: ['wizards', 'lead to award'] },
  { label: 'Settings', path: '/settings' },
  { label: 'Audit Trail', path: '/settings/audit' },
  { label: 'About this prototype', path: '/about' },
  // Module sub-pages (most-trafficked)
  { label: 'Chart of Accounts', path: '/finance/coa', keywords: ['coa'] },
  { label: 'General Ledger', path: '/finance/gl', keywords: ['gl', 'journal'] },
  { label: 'Accounts Payable', path: '/finance/ap', keywords: ['ap'] },
  { label: 'Accounts Receivable', path: '/finance/ar', keywords: ['ar', 'aging'] },
  { label: 'Cash Flow', path: '/finance/cash-flow' },
  { label: 'Project Profitability', path: '/contracts/profitability', keywords: ['margin'] },
  { label: 'Variation Orders', path: '/contracts/variation-orders', keywords: ['vo'] },
  { label: 'Interim Payment Certificates', path: '/contracts/ipc', keywords: ['ipc'] },
  { label: 'Purchase Orders', path: '/contracts/po', keywords: ['po'] },
  { label: 'Claims', path: '/contracts/claims' },
  { label: 'Projects', path: '/engineering/projects' },
  { label: 'Drawings', path: '/engineering/drawings' },
  { label: 'Submittals', path: '/engineering/submittals' },
  { label: 'Material Requests', path: '/engineering/material-requests', keywords: ['mr'] },
  { label: 'Employees', path: '/hr/employees' },
  { label: 'Org Chart', path: '/hr/org-chart' },
  { label: 'Payroll', path: '/hr/payroll' },
  { label: 'Document Expiry', path: '/hr/document-expiry', keywords: ['iqama', 'visa', 'passport'] },
  { label: 'Leads', path: '/marketing/leads', keywords: ['kanban', 'pipeline'] },
  { label: 'Tenders', path: '/marketing/tenders' },
  { label: 'Marketing Reports', path: '/marketing/reports' },
];

const RECENTS_KEY = 'ga-erp-cmdk-recents';
const RECENTS_LIMIT = 8;

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeRecents(ids: string[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(ids.slice(0, RECENTS_LIMIT)));
  } catch {
    /* ignore quota / disabled storage */
  }
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useMockStore();
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Build the search index lazily when the palette opens. Closing the
  // palette discards the indexed snapshot — opening again re-reads the
  // store, so fixture resets surface immediately.
  const index = useMemo<CommandItem[]>(() => {
    if (!open) return [];
    const items: CommandItem[] = [];
    for (const p of PAGE_INDEX) {
      items.push({
        id: `page:${p.path}`,
        label: p.label,
        category: 'page',
        iconKind: 'page',
        path: p.path,
        ...(p.keywords ? { keywords: p.keywords } : {}),
      });
    }
    for (const project of state.projects.slice(0, 50)) {
      items.push({
        id: `project:${project.id}`,
        label: `${project.code} — ${project.name}`,
        detail: project.city,
        category: 'project',
        iconKind: 'project',
        path: '/engineering/projects',
        keywords: [project.code.toLowerCase()],
      });
    }
    // Resolve the counterparty name from clients (client contracts) or
    // suppliers (subcontracts) since Contract holds only IDs.
    const clientById = new Map(state.clients.map((cl) => [cl.id, cl.name]));
    const supplierById = new Map(state.suppliers.map((sp) => [sp.id, sp.name]));
    for (const c of state.contracts.slice(0, 50)) {
      const counterparty =
        c.type === 'subcontract'
          ? (c.subcontractorId ? supplierById.get(c.subcontractorId) : undefined) ?? '—'
          : clientById.get(c.clientId) ?? '—';
      items.push({
        id: `contract:${c.id}`,
        label: `${c.code} — ${counterparty}`,
        detail: c.type === 'subcontract' ? 'Subcontract' : 'Client contract',
        category: 'contract',
        iconKind: 'contract',
        path: c.type === 'subcontract' ? '/contracts/subcontracts' : '/contracts/clients',
        keywords: [c.code.toLowerCase()],
      });
    }
    for (const e of state.employees.slice(0, 50)) {
      items.push({
        id: `employee:${e.id}`,
        label: e.fullName,
        detail: e.roleTitle,
        category: 'employee',
        iconKind: 'employee',
        path: '/hr/employees',
        keywords: [e.employeeNumber.toLowerCase()],
      });
    }
    for (const l of state.leads.slice(0, 50)) {
      items.push({
        id: `lead:${l.id}`,
        label: l.title,
        detail: l.stage,
        category: 'lead',
        iconKind: 'lead',
        path: '/marketing/leads',
      });
    }
    for (const tnd of state.tenders.slice(0, 50)) {
      items.push({
        id: `tender:${tnd.id}`,
        label: tnd.title,
        detail: tnd.stage,
        category: 'tender',
        iconKind: 'tender',
        path: '/marketing/tenders',
      });
    }
    return items;
  }, [open, state.projects, state.contracts, state.employees, state.leads, state.tenders]);

  // Filter + rank. Empty query → recents (resolved against the index).
  const visible = useMemo<CommandItem[]>(() => {
    if (!open) return [];
    if (!query.trim()) {
      // Recents view: pull last visited items in order, fall back to a
      // "popular pages" hint when the user has no history yet.
      const byId = new Map(index.map((i) => [i.id, i]));
      const fromRecents = recents
        .map((id) => byId.get(id))
        .filter((i): i is CommandItem => Boolean(i));
      if (fromRecents.length > 0) return fromRecents;
      return [
        'page:/dashboard',
        'page:/engineering/projects',
        'page:/contracts/profitability',
        'page:/marketing/leads',
        'page:/workflows',
      ]
        .map((id) => byId.get(id))
        .filter((i): i is CommandItem => Boolean(i));
    }
    const q = query.toLowerCase().trim();
    const scored: Array<{ item: CommandItem; score: number }> = [];
    for (const item of index) {
      const label = item.label.toLowerCase();
      const detail = (item.detail ?? '').toLowerCase();
      const kw = (item.keywords ?? []).join(' ').toLowerCase();
      let score = 0;
      if (label === q) score = 1000;
      else if (label.startsWith(q)) score = 500;
      else if (label.includes(q)) score = 300;
      else if (detail.includes(q)) score = 150;
      else if (kw.includes(q)) score = 100;
      if (score > 0) scored.push({ item, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 50).map((s) => s.item);
  }, [open, index, query, recents]);

  // Reset state on open / close.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      setRecents(readRecents());
      // Defer focus to next tick so the Modal has actually mounted.
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keep selected index in bounds when filter changes.
  useEffect(() => {
    if (selectedIdx >= visible.length) setSelectedIdx(0);
  }, [visible.length, selectedIdx]);

  // Scroll the selected row into view.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.querySelector(
      `[data-cmdk-idx="${selectedIdx}"]`,
    ) as HTMLElement | null;
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const handleSelect = (item: CommandItem): void => {
    // Update recents — most recent first, dedupe.
    const next = [item.id, ...recents.filter((r) => r !== item.id)].slice(0, RECENTS_LIMIT);
    writeRecents(next);
    onClose();
    navigate(item.path);
  };

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>): void => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, visible.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const item = visible[selectedIdx];
      if (item) handleSelect(item);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnHidden
      width={600}
      styles={{ body: { padding: 0 } }}
      style={{ top: 100 }}
    >
      <Input
        ref={inputRef as React.Ref<typeof Input> as never}
        size="large"
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.4)' }} />}
        placeholder={t('cmdk.placeholder', 'Jump to a page, project, contract, person, lead…')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSelectedIdx(0);
        }}
        onKeyDown={handleKeyDown}
        bordered={false}
        style={{
          fontSize: 16,
          padding: '14px 20px',
          borderBottom: '1px solid #efe9dd',
        }}
      />
      <div
        ref={listRef}
        style={{
          maxHeight: 420,
          overflowY: 'auto',
          padding: '8px 0',
        }}
      >
        {visible.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary">
                  {t('cmdk.noResults', 'No matches. Try a project code, person name, or page.')}
                </Text>
              }
            />
          </div>
        ) : (
          <>
            {!query && recents.length === 0 && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: 'rgba(26, 31, 27, 0.45)',
                  padding: '4px 20px 8px',
                }}
              >
                <HistoryOutlined style={{ marginInlineEnd: 6 }} />
                {t('cmdk.suggested', 'Suggested')}
              </div>
            )}
            {!query && recents.length > 0 && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: 'rgba(26, 31, 27, 0.45)',
                  padding: '4px 20px 8px',
                }}
              >
                <HistoryOutlined style={{ marginInlineEnd: 6 }} />
                {t('cmdk.recent', 'Recent')}
              </div>
            )}
            {visible.map((item, idx) => {
              const meta = CATEGORY_META[item.iconKind];
              const isSelected = idx === selectedIdx;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-cmdk-idx={idx}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 20px',
                    background: isSelected ? 'rgba(31, 77, 58, 0.08)' : 'transparent',
                    border: 'none',
                    borderInlineStart: `3px solid ${isSelected ? semantic.brand.primary : 'transparent'}`,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                    textAlign: 'start',
                    transition: 'background 80ms ease',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: `${meta.color}15`,
                      color: meta.color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {meta.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'rgba(26, 31, 27, 0.9)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </div>
                    {item.detail && (
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(26, 31, 27, 0.55)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 1,
                        }}
                      >
                        {item.iconKind === 'project' && <EnvironmentOutlined />}
                        {item.detail}
                      </div>
                    )}
                  </div>
                  <Tag
                    style={{
                      margin: 0,
                      borderColor: meta.color,
                      background: `${meta.color}10`,
                      color: meta.color,
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {meta.label}
                  </Tag>
                  {isSelected && (
                    <ArrowRightOutlined
                      style={{
                        color: semantic.brand.primary,
                        fontSize: 12,
                        marginInlineStart: 4,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </>
        )}
      </div>
      {/* Footer hint strip */}
      <div
        style={{
          borderTop: '1px solid #efe9dd',
          padding: '8px 16px',
          fontSize: 11,
          color: 'rgba(26, 31, 27, 0.5)',
          display: 'flex',
          gap: 16,
          background: '#FAF8F4',
        }}
      >
        <span>
          <kbd style={kbdStyle}>↑</kbd>
          <kbd style={kbdStyle}>↓</kbd> {t('cmdk.navigate', 'navigate')}
        </span>
        <span>
          <kbd style={kbdStyle}>↵</kbd> {t('cmdk.select', 'select')}
        </span>
        <span>
          <kbd style={kbdStyle}>esc</kbd> {t('cmdk.close', 'close')}
        </span>
      </div>
    </Modal>
  );
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '0 5px',
  marginInlineEnd: 4,
  background: '#fff',
  border: '1px solid #efe9dd',
  borderRadius: 3,
  fontSize: 10,
  fontFamily: 'inherit',
  color: 'rgba(26, 31, 27, 0.7)',
};
